import { useEffect, useState, useMemo} from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import { apiFetch, downloadPdf } from '../../api';
import '../master/Products.css';
import DateTimeText from '../../components/DateTimeText';
import { FaFilter } from 'react-icons/fa';
import Pagination from "../../components/Pagination";

export default function MaterialReturnIndex() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(null);

  // Filter states
  const [showminnoFilter, setShowminnoFilter] = useState(false);
  const [showCustomerFilter, setShowCustomerFilter] = useState(false);
  const [minnoFilter, setminnoFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [filteredRows, setFilteredRows] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const totalItems = useMemo(() => {
    return filteredRows.length;
  }, [filteredRows]);

  const totalPages = useMemo(() => {
    return Math.ceil(totalItems / rowsPerPage);
  }, [totalItems, rowsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const handlePaginatedUsers = useMemo(() => {
    return filteredRows.slice(
      (currentPage - 1) * rowsPerPage,
      currentPage * rowsPerPage
    );
  }, [filteredRows, currentPage, rowsPerPage]);


  /* =========================
     FETCH MATERIAL ISSUE NOTES
     ========================= */
  useEffect(() => {
    fetchMaterialIssues();
  }, []);

  const fetchMaterialIssues = async () => {
    try {
      const res = await apiFetch('/api/material-return/list');

      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];

      setRows(list);
      setFilteredRows(list);
    } catch (err) {
      console.error('Failed to load material Return notes', err);
      setRows([]);
      setFilteredRows([]);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    let filtered = rows;

    if (minnoFilter) {
      filtered = filtered.filter(row =>
        row.min_no?.toLowerCase().includes(minnoFilter.toLowerCase())
      );
    }

    if (customerFilter) {
      filtered = filtered.filter(row =>
        row.person_issuing?.toLowerCase().includes(customerFilter.toLowerCase())
      );
    }

    setFilteredRows(filtered);
  }, [minnoFilter, customerFilter, rows]);

  const handleminnoFilterChange = (e) => {
    setminnoFilter(e.target.value);
  };

  const handleCustomerFilterChange = (e) => {
    setCustomerFilter(e.target.value);
  };


  /* =========================
     DOWNLOAD PDF
     ========================= */
  const handleDownloadPdf = async (id, minNo) => {
    try {
      setDownloadingPdf(id);
      
      const blob = await downloadPdf(`/pdf-return.php?id=${id}`);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `Material_Return_Note_${minNo}.pdf`;
      
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('PDF download failed:', error);
      alert(`PDF download failed: ${error.message}`);
    } finally {
      setDownloadingPdf(null);
    }
  };

  /* =========================
     UI
     ========================= */
  if (loading) {
    return (
      <Layout>
        <h2>Loading material Return notes...</h2>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* ================= HEADER ================= */}
      <div className="products-toolbar">
        <div>
          <h1 className="products-title">Material Return Notes</h1>
          <p className="products-subtitle">
            Issue / dispatch material records
          </p>
        </div>

        <div>
          <button
            className="btn-primary"
            onClick={() => navigate('/material-return-note')}
          >
            + Create Material Return Note
          </button>
        </div>
      </div>

      {/* ================= TABLE ================= */}

        <div className="products-table-wrapper">
          <table className="products-table">
            <thead>
              <tr>
                <th>ID</th>
                 <th>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    MRN No
                    <FaFilter
                      style={{ cursor: 'pointer', fontSize: '12px', color: minnoFilter ? '#3b82f6' : '#6b7280' }}
                      onClick={() => setShowminnoFilter(!showminnoFilter)}
                      title="Filter by MRN No"
                    />
                  </div>
                  {showminnoFilter && (
                    <input
                      type="text"
                      placeholder="Filter MRN No..."
                      value={minnoFilter}
                      onChange={handleminnoFilterChange}
                      onBlur={() => setTimeout(() => setShowminnoFilter(false), 200)}
                      autoFocus
                      style={{
                        marginTop: '8px',
                        padding: '4px 8px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        width: '80%',
                        height: '25px'
                      }}
                    />
                  )}
                </th>
                <th>Date</th>
                 <th>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    Person Issuing
                    <FaFilter
                      style={{ cursor: 'pointer', fontSize: '12px', color: customerFilter ? '#3b82f6' : '#6b7280' }}
                      onClick={() => setShowCustomerFilter(!showCustomerFilter)}
                      title="Filter by Person Issuing"
                    />
                  </div>
                  {showCustomerFilter && (
                    <input
                      type="text"
                      placeholder="Filter Person Issuing..."
                      value={customerFilter}
                      onChange={handleCustomerFilterChange}
                      onBlur={() => setTimeout(() => setShowCustomerFilter(false), 200)}
                      autoFocus
                      style={{
                        marginTop: '8px',
                        padding: '4px 8px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        width: '80%',
                        height: '25px'
                      }}
                    />
                  )}
                </th>
                <th>Department</th>
                <th>Total Items</th>
                <th>Total Qty</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
               {handlePaginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                      No Material Return Notes found
                    </td>
                  </tr>
              ) : (
              handlePaginatedUsers?.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>

                  <td>
                    {row.min_no}
                    {row.auto_generated && (
                      <span style={{ marginLeft: 6, fontSize: 11, color: '#6b7280' }}>
                        (Auto)
                      </span>
                    )}
                  </td>

                  <td>
                    <DateTimeText value={row.date_time} />
                  </td>

                  <td>{row.person_issuing}</td>

                  <td>{row.department_issuing || '-'}</td>

                  <td>{row.total_items ?? 0}</td>

                  <td>{row.total_qty ?? 0}</td>

                  <td>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        background:
                          row.status === 'APPROVED'
                            ? '#d1fae5'
                            : row.status === 'REJECTED'
                            ? '#fee2e2'
                            : '#fef3c7',
                        color:
                          row.status === 'APPROVED'
                            ? '#065f46'
                            : row.status === 'REJECTED'
                            ? '#991b1b'
                            : '#92400e',
                      }}
                    >
                      {row.status}
                    </span>
                  </td>

                  <td>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        className="btn-link"
                        onClick={() =>
                          navigate(`/material-return-note/${row.id}`)
                        }
                      >
                        View
                      </button>
                      <button
                        className="btn-link"
                        onClick={() => handleDownloadPdf(row.id, row.min_no)}
                        disabled={downloadingPdf === row.id}
                        style={{
                          opacity: downloadingPdf === row.id ? 0.6 : 1,
                          cursor: downloadingPdf === row.id ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {downloadingPdf === row.id ? 'Downloading...' : 'PDF'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
            </tbody>
          </table>
        </div>
         <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
    </Layout>
  );
}